import { DELIVERABLES_CRUD_ACTION_CREATORS } from '../actions/deliverables_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { ROLE_TYPES } from '../utils/DataConstants';
import { DELIVERABLES_GRAPH_TC } from '../utils/DeliverableUtils';

const fetchUtils = new FetchUtils();

// Real backend: /api/v1/{buyer|supplier}/project/project_deliverables
// (openwrench-app claude/project-deliverables-tasks). GET responses are hydrated
// with server-computed workOrderMetrics / taskMetrics / budgetUsed.
export const deliverablesRestCrudThunksForBuyer = new RestCrudThunks(
	'project_deliverables',
	'project_deliverable',
	'projectDeliverable',
	'projectDeliverables',
	DELIVERABLES_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'project/'
);

export const deliverablesRestCrudThunksForSupplier = new RestCrudThunks(
	'project_deliverables',
	'project_deliverable',
	'projectDeliverable',
	'projectDeliverables',
	DELIVERABLES_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'project/'
);

// Dedicated idempotent link/unlink endpoints (one work order at a time). The
// PATCH returns the updated, re-hydrated deliverable (workOrders + metrics), so
// we push it straight into the detail via fetchOneSuccess.
const linkWorkOrderThunk = (userType, link) => (deliverableId, workOrderId) => {
	const verb = link ? 'link_work_order' : 'unlink_work_order';
	const apiUrl = `/api/v1/${userType}/project/project_deliverables/${deliverableId}/${verb}/${workOrderId}`;
	return (dispatch) =>
		new Promise((resolve, reject) => {
			fetchUtils
				.patch(getBackendUri() + apiUrl, { data: {} })
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status === 200) {
						dispatch(DELIVERABLES_CRUD_ACTION_CREATORS.fetchOneSuccess(json.data));
						resolve(json.data);
					} else {
						reject(nullSafeGet('message', json));
					}
				})
				.catch((error) => reject(error.message));
		});
};

// Sets a deliverable's due date AND shifts every transitively dependent
// deliverable by the same delta (the dependsOnDeliverableId chain). A plain PUT
// does not cascade, so this endpoint is the only way to move a date and take the
// downstream chain with it — and it has to run BEFORE any PUT that also carries
// dueDate, since the server derives the delta from the stored date.
//
// The response is every deliverable it touched, this one first. Each is pushed
// into records via fetchOneSuccess so an open detail pane is correct immediately;
// the caller still refetches the list to pick up the new ordering.
const shiftDueDateThunk = (userType) => (deliverableId, dueDate) => {
	const apiUrl = `/api/v1/${userType}/project/project_deliverables/${deliverableId}/due_date`;
	return (dispatch) =>
		new Promise((resolve, reject) => {
			fetchUtils
				.patch(getBackendUri() + apiUrl, { data: { dueDate } })
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status === 200) {
						const updated = nullSafeGetOrElse('data', json, []);
						updated.forEach((deliverable) =>
							dispatch(DELIVERABLES_CRUD_ACTION_CREATORS.fetchOneSuccess(deliverable))
						);
						resolve(updated);
					} else {
						reject(nullSafeGet('message', json));
					}
				})
				.catch((error) => reject(error.message));
		});
};

// Role factory rather than a pre-bound ForBuyer/ForSupplier pair: the caller passes
// its own userType, so there is no per-call-site ternary to get wrong.
// (The link/unlink pair below predates this and keeps its bound form.)
export const shiftDeliverableDueDate = shiftDueDateThunk;

export const linkDeliverableWorkOrderForBuyer = linkWorkOrderThunk(ROLE_TYPES.BUYER, true);
export const linkDeliverableWorkOrderForSupplier = linkWorkOrderThunk(ROLE_TYPES.SUPPLIER, true);
export const unlinkDeliverableWorkOrderForBuyer = linkWorkOrderThunk(ROLE_TYPES.BUYER, false);
export const unlinkDeliverableWorkOrderForSupplier = linkWorkOrderThunk(ROLE_TYPES.SUPPLIER, false);

// Page size for fetchAllProjectDeliverables. 50 matches the deliverables list's own
// page size and the backend's warn-above-50 threshold (PaginationExtension).
const ALL_DELIVERABLES_PAGE_SIZE = 50;

// Every deliverable of a project, unfiltered, into DELIVERABLES_GRAPH_TC. The
// dependency helpers treat that collection as the complete graph, and the project
// Work Orders tab maps each work order to its deliverable from it, so one page is not
// enough: keep going until a page comes back short, appending after the first.
//
// GET /project/project_deliverables does not honour no_pagination — the route lands
// on CRUHelper.retrieveAll, which reads request.getPagination unconditionally and
// defaults to a limit of 10 — so paging is the only way to get all of them.
export const fetchAllProjectDeliverables = (userType) => (projectId) => (dispatch, getState) => {
	const thunks =
		userType === ROLE_TYPES.SUPPLIER
			? deliverablesRestCrudThunksForSupplier
			: deliverablesRestCrudThunksForBuyer;
	const fetchPage = (current, fetchedSoFar) =>
		dispatch(
			thunks.read(
				{ projectId },
				DELIVERABLES_GRAPH_TC,
				{ current, pageSize: ALL_DELIVERABLES_PAGE_SIZE },
				null,
				null,
				current > 1
			)
		).then((rows) => {
			const fetched = fetchedSoFar + nullSafeGetOrElse('length', rows, 0);
			// Two stop conditions, no arbitrary page cap: a short page is the last one,
			// and the API's own count says when we have them all. Either alone would do
			// for a well-behaved endpoint; together they also terminate if one of them
			// misreports.
			const total = nullSafeGetOrElse(
				`${DELIVERABLES_GRAPH_TC}.count`,
				getState().deliverables,
				fetched
			);
			return nullSafeGetOrElse('length', rows, 0) === ALL_DELIVERABLES_PAGE_SIZE && fetched < total
				? fetchPage(current + 1, fetched)
				: rows;
		});
	return fetchPage(1, 0);
};
